import { useEffect, useState } from "react";
import { MapPicker } from "@/components/MapPicker";
import { DataTable } from "@/components/fleet/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { api, kina } from "@/lib/api";
import { pomToday } from "@/lib/useRoute";

const time = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-AU", { timeZone: "Pacific/Port_Moresby", hour: "2-digit", minute: "2-digit" }) : "—");

const TrackView = ({ row, date }) => {
  const [t, setT] = useState(null);
  useEffect(() => {
    api.get("/fleet/driver-track", { params: { driver_id: row.driver_id, date } }).then((r) => setT(r.data)).catch(() => {});
  }, [row.driver_id, date]);
  if (!t) return <div className="h-80 animate-pulse rounded-2xl bg-white" />;
  const end = t.points[t.points.length - 1];
  const fit = t.points.length ? [...t.points, [t.base.lat, t.base.lng]] : [[t.base.lat, t.base.lng]];
  return (
    <div data-testid="shift-track-view" className="space-y-2">
      <p className="text-sm font-semibold text-slate-900">{row.name} · route driven on {date} <span className="font-normal text-slate-500">({t.points.length} GPS points)</span></p>
      <MapPicker testId="shift-track-map" track={t.points} base={t.base} taxi={end ? { lat: end[0], lng: end[1] } : null} fitPoints={fit} fitKey={`${row.driver_id}-${date}`} className="h-[420px]" />
    </div>
  );
};

export const ShiftReportPanel = () => {
  const [date, setDate] = useState(pomToday());
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => {
    setData(null);
    setSel(null);
    api.get("/fleet/shift-report", { params: { date } }).then((r) => setData(r.data)).catch(() => setData({ rows: [] }));
  }, [date]);
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-4">
        <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Report date
          <input data-testid="shift-report-date-input" type="date" value={date} max={pomToday()} onChange={(e) => setDate(e.target.value)} className="mt-1 block h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm" />
        </label>
        {data?.base && <p className="text-sm text-slate-500">Base: <b className="text-slate-800">{data.base.name}</b> · {data.base.radius_m} m radius</p>}
      </div>
      <DataTable
        testId="shift-report-table"
        rows={data?.rows}
        empty="No driver GPS activity or trips on this date."
        cols={[
          ["Driver", (r) => <span className="font-semibold text-slate-900">{r.taxi_no ? `#${r.taxi_no} · ` : ""}{r.name}</span>],
          ["First seen", (r) => time(r.first_seen)],
          ["Left base", (r) => time(r.left_base_at)],
          ["Trips", (r) => r.trips],
          ["Trip km", (r) => r.trip_km],
          ["Fares", (r) => kina(r.fares)],
          ["GPS km driven", (r) => <b className="font-mono-num">{r.km_driven}</b>],
          ["Back at base", (r) => time(r.returned_at)],
          ["End: from base", (r) => (r.end_km_from_base != null ? `${r.end_km_from_base} km` : "—")],
          ["Shift end", (r) => <StatusBadge status={r.status} testId={`shift-status-${r.driver_id}`} />],
          ["", (r) => r.pings > 0 && <button data-testid={`view-track-${r.driver_id}`} onClick={() => setSel(r)} className="rounded-lg border border-blue-200 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50">View route</button>],
        ]}
      />
      {sel && <TrackView row={sel} date={date} />}
    </div>
  );
};
